import { Member } from "@shared/types/profile.types";
import React from "react";
import { Link } from "react-router-dom";

import { ConnectionButton } from "@shared/components/ConnectionButton";
import { getProfileThumbnail } from "@shared/hooks/useProfile";
import api from "@shared/services/api";
import { getUserProfilePath } from "@shared/utils/navigation";
import theme from "@shared/utils/themeColors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import EditMenu from "../../../components/DropdownEditMenu";

interface MemberCardProps {
  member: Member;
}

export const MemberCard: React.FC<MemberCardProps> = ({ member }) => {
  const queryClient = useQueryClient();
  const { id, name } = member;

  const cancelMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      await api.delete(`/connections/${connectionId}/cancel`);
    },
    onSuccess: () => {
      // Invalidate both connections and members queries
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["members"] });
      toast.success("Connection cancelled successfully");
    },
    onError: () => {
      toast.error("Error cancelling connection");
    },
  });

  const handleCancelConnection = async (connectionId: string) => {
    if (!connectionId) return;
    cancelMutation.mutate(connectionId);
  };

  return (
    <div
      className={`col-span-full sm:col-span-6 xl:col-span-3 ${theme.bg.primary} shadow-sm rounded-xl`}
    >
      <div className="flex flex-col h-full">
        {/* Card top */}
        <div className="p-5">
          {/* Menu button */}
          {member.connection &&
            (member.connection.isPending || member.connection.isConnected) && (
              <div className="relative">
                <EditMenu
                  align="right"
                  className="absolute top-0 right-0 inline-flex"
                >
                  <li>
                    <button
                      className={`font-medium text-sm ${theme.text.error} hover:text-red-600 flex py-1 px-3 items-center disabled:opacity-50`}
                      onClick={() =>
                        handleCancelConnection(member.connection?.connectionId)
                      }
                      disabled={cancelMutation.isPending}
                    >
                      {cancelMutation.isPending
                        ? "Cancelling..."
                        : member.connection.isPending
                        ? "Cancel request"
                        : "Remove connection"}
                    </button>
                  </li>
                </EditMenu>
              </div>
            )}
          {/* Image + name */}
          <header>
            <div className="flex justify-center mb-2">
              <Link
                className="relative inline-flex items-start"
                to={getUserProfilePath(id)}
              >
                <div
                  className="absolute top-0 right-0 -mr-2 bg-white dark:bg-gray-700 rounded-full shadow"
                  aria-hidden="true"
                >
                  <svg
                    className="w-8 h-8 fill-current text-yellow-500"
                    viewBox="0 0 32 32"
                  >
                    <path d="M21 14.077a.75.75 0 01-.75-.75 1.5 1.5 0 00-1.5-1.5.75.75 0 110-1.5 1.5 1.5 0 001.5-1.5.75.75 0 111.5 0 1.5 1.5 0 001.5 1.5.75.75 0 010 1.5 1.5 1.5 0 00-1.5 1.5.75.75 0 01-.75.75zM14 24.077a1 1 0 01-1-1 4 4 0 00-4-4 1 1 0 110-2 4 4 0 004-4 1 1 0 012 0 4 4 0 004 4 1 1 0 010 2 4 4 0 00-4 4 1 1 0 01-1 1z" />
                  </svg>
                </div>
                <img
                  className="rounded-full"
                  src={getProfileThumbnail(member, member.name)}
                  width="64"
                  height="64"
                  alt={name}
                />
              </Link>
            </div>
            <div className="text-center">
              <Link
                className={`inline-flex ${theme.text.primary} hover:text-gray-900 dark:hover:text-white`}
                to={getUserProfilePath(id)}
              >
                <h2 className="text-xl leading-snug justify-center font-semibold">
                  {name}
                </h2>
              </Link>
            </div>
            {/* Location */}
            {member.location && (
              <div className="flex justify-center items-center">
                <span
                  className={`text-sm font-medium text-gray-400 dark: ${theme.text.muted}`}
                >
                  <svg
                    className={`fill-current shrink-0 text-gray-400 dark: ${theme.text.muted}`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 8.992a2 2 0 1 1-.002-3.998A2 2 0 0 1 8 8.992Zm-.7 6.694c-.1-.1-4.2-3.696-4.2-3.796C1.7 10.69 1 8.892 1 6.994 1 3.097 4.1 0 8 0s7 3.097 7 6.994c0 1.898-.7 3.697-2.1 4.996-.1.1-4.1 3.696-4.2 3.796-.4.3-1 .3-1.4-.1Zm-2.7-4.995L8 13.688l3.4-2.997c1-1 1.6-2.198 1.6-3.597 0-2.798-2.2-4.996-5-4.996S3 4.196 3 6.994c0 1.399.6 2.698 1.6 3.697 0-.1 0-.1 0 0Z" />
                  </svg>
                </span>{" "}
                <span className="truncate">{member.location}</span>
              </div>
            )}
          </header>
          {/* Bio */}
          <div className="text-center mt-2">
            <div className="text-sm">{member.title}</div>
          </div>
        </div>
        {/* Card footer */}
        <div className="border-t border-gray-100 dark:border-gray-700/60">
          <ConnectionButton
            userId={member.id}
            initialState={member.connection}
          />
        </div>
      </div>
    </div>
  );
};
